import { RigFracTimeScale } from "constants/charts.enums";

import { IChartResult } from "models/model";

import { ChartDataTableColumn, ChartDataTableRow } from "../hooks/useDataTableData";

const BUCKET_COLUMN_KEY = "bucket";

// Descriptive bucket-column header per time scale — the generic "Time" reads as
// unhelpful next to labels like "2025-Q3" (kflemmer QA, EVA-76). Custom ranges
// are bucketed weekly, so they share the Weekly header.
const BUCKET_COLUMN_HEADER: Record<RigFracTimeScale, string> = {
  [RigFracTimeScale.Quarterly]: "Year-QTR",
  [RigFracTimeScale.Monthly]: "Year-Month",
  [RigFracTimeScale.Weekly]: "Week",
  [RigFracTimeScale.Custom]: "Week"
};

// NOTE: Changing this logic should result in changes to the chart-svc backend export logic.
/**
 * Client data table for the Rig/Frac Activity chart (EVA-76 US4): one row per
 * time bucket, first column the bucket label, then one column per group (bar)
 * series plus the overlay line series — matching the stacked-bar export
 * column conventions of the chart-svc RigFracActivity export route.
 *
 * `timeScale` drives the descriptive header for the bucket column (e.g.
 * "Year-QTR" for quarterly); when unknown it falls back to the backend x-axis
 * title (currently "Time").
 */
const getRigFracActivityDataTable = (
  responseData: IChartResult,
  timeScale?: Maybe<RigFracTimeScale>
): { columns: ChartDataTableColumn[]; rows: ChartDataTableRow[] } => {
  const bucketLabels = (responseData?.layout?.xAxis?.data ?? []) as string[];
  const series = responseData?.series ?? [];

  const bucketColumnName =
    (timeScale && BUCKET_COLUMN_HEADER[timeScale]) ??
    responseData?.layout?.xAxis?.title ??
    "Time";

  const columns: ChartDataTableColumn[] = [
    {
      key: BUCKET_COLUMN_KEY,
      name: bucketColumnName,
      resizable: false
    },
    ...series.map((s) => ({
      key: s.label,
      name: s.label,
      resizable: false
    }))
  ];

  const rows: ChartDataTableRow[] = bucketLabels.map((label, bucketIndex) => {
    const row: ChartDataTableRow = { [BUCKET_COLUMN_KEY]: label };
    series.forEach((s) => {
      const value = s.y?.[bucketIndex];
      row[s.label] = typeof value === "number" ? value : "";
    });
    return row;
  });

  return {
    columns,
    rows
  };
};

export default getRigFracActivityDataTable;
